import { useAuthStore } from "@/stores/auth.store";

// Declare runtime config type
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      API_URL?: string;
    };
  }
}

// Priority: runtime config > env variable > empty (proxy mode)
const API_URL = window.__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || "";

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth, ...fetchOptions } = options;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Adiciona token de autenticação se disponível
  if (!skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // Handle 401 - Unauthorized
  // Só faz logout se não for uma rota pública (skipAuth)
  // Para rotas de login, o 401 significa credenciais inválidas, não sessão expirada
  if (response.status === 401 && !skipAuth) {
    useAuthStore.getState().logout();
    window.location.href = "/login";
    throw new Error("Sessão expirada");
  }

  // Handle 204 No Content (comum em DELETE)
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Erro na requisição");
  }

  return data;
}

// Public API (sem autenticação)
export const publicApi = {
  accessRequest: (data: {
    cnpj: string;
    razaoSocial: string;
    nomeFantasia: string;
    email: string;
    telefone: string;
    contatoNome: string;
  }) =>
    request<{ success: boolean; message: string; companyId: string }>(
      "/api/public/access-request",
      {
        method: "POST",
        body: JSON.stringify(data),
        skipAuth: true,
      }
    ),
};

// Auth API
export interface LoginSession {
  accessToken: string;
  user: any;
  /** só na confirmação do cadastro do 2FA: mostrados uma única vez */
  recoveryCodes?: string[];
  /** só quando o login usou um código de recuperação */
  recoveryCodesRemaining?: number;
}

/** usuário interno: a senha certa abre a etapa do 2FA (cadastro ou código) */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  stage: "setup" | "verify";
  challengeToken: string;
  expiresInSeconds: number;
  setup?: { secret: string; otpauthUrl: string; qrCodeDataUrl: string };
}

export function isTwoFactorChallenge(
  response: LoginSession | TwoFactorChallenge
): response is TwoFactorChallenge {
  return (response as TwoFactorChallenge).twoFactorRequired === true;
}

export const authApi = {
  login: (email: string, password: string, type?: "internal" | "company") =>
    request<LoginSession | TwoFactorChallenge>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, type }),
      skipAuth: true,
    }),

  confirmTwoFactorSetup: (challengeToken: string, code: string) =>
    request<LoginSession>("/api/auth/2fa/setup/confirm", {
      method: "POST",
      body: JSON.stringify({ challengeToken, code }),
      skipAuth: true,
    }),

  verifyTwoFactor: (challengeToken: string, code: string) =>
    request<LoginSession>("/api/auth/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ challengeToken, code }),
      skipAuth: true,
    }),

  me: () => request<any>("/api/auth/me"),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    request<{ success: boolean; message: string }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Companies API
export const companiesApi = {
  list: (includeWallet?: boolean) =>
    request<any[]>(`/api/admin/companies${includeWallet ? "?includeWallet=true" : ""}`),

  getById: (id: string, includeWallet?: boolean) =>
    request<any>(`/api/admin/companies/${id}${includeWallet ? "?includeWallet=true" : ""}`),

  getWallet: (id: string) => request<any>(`/api/admin/companies/${id}/wallet`),

  create: (data: any) =>
    request<any>("/api/admin/companies", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/companies/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  approve: (id: string) =>
    request<any>(`/api/admin/companies/${id}/approve`, {
      method: "POST",
    }),

  suspend: (id: string) =>
    request<any>(`/api/admin/companies/${id}/suspend`, {
      method: "POST",
    }),

  reactivate: (id: string) =>
    request<any>(`/api/admin/companies/${id}/reactivate`, {
      method: "POST",
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/companies/${id}`, {
      method: "DELETE",
    }),

  // Features
  getFeatures: (companyId: string) =>
    request<{
      companyId: string;
      companyName: string;
      features: Array<{
        featureName: string;
        displayName: string;
        description: string;
        isEnabled: boolean;
        enabledAt?: string;
        enabledByName?: string;
        disabledAt?: string;
        notes?: string;
      }>;
    }>(`/api/admin/companies/${companyId}/features`),

  updateFeature: (companyId: string, featureName: string, data: { isEnabled: boolean; notes?: string }) =>
    request<any>(`/api/admin/companies/${companyId}/features/${featureName}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// Features API (admin)
export const featuresApi = {
  list: () =>
    request<Array<{ name: string; displayName: string; description: string }>>("/api/admin/features"),

  getCompaniesWithFeature: (featureName: string) =>
    request<{
      featureName: string;
      displayName: string;
      companies: Array<{
        id: string;
        razaoSocial: string;
        cnpj: string;
        isEnabled: boolean;
        enabledAt?: string;
      }>;
      totalEnabled: number;
      totalCompanies: number;
    }>(`/api/admin/features/${featureName}/companies`),
};

// Company Features API (para área da empresa)
export const companyFeaturesApi = {
  getEnabled: () => request<string[]>("/api/company/features"),
};

// Users API
export const usersApi = {
  list: (companyId?: string) =>
    request<any[]>(
      `/api/admin/users${companyId ? `?companyId=${companyId}` : ""}`
    ),

  getById: (id: string) => request<any>(`/api/admin/users/${id}`),

  create: (data: any) =>
    request<any>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/users/${id}`, {
      method: "DELETE",
    }),

  invite: (data: {
    email: string;
    name: string;
    companyId: string;
    role: string;
    cpf?: string;
  }) =>
    request<any>("/api/admin/users/invite", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  toggleActive: (id: string) =>
    request<any>(`/api/admin/users/${id}/toggle-active`, {
      method: "POST",
    }),
};

// Internal Users API
export const internalUsersApi = {
  list: () => request<any[]>("/api/admin/internal-users"),

  getById: (id: string) => request<any>(`/api/admin/internal-users/${id}`),

  create: (data: any) =>
    request<any>("/api/admin/internal-users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/internal-users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/internal-users/${id}`, {
      method: "DELETE",
    }),
};

// Email Config API
export const emailApi = {
  getConfig: () => request<any>("/api/admin/email/config"),

  updateConfig: (data: any) =>
    request<any>("/api/admin/email/config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  test: (to: string, useInviteTemplate?: boolean) =>
    request<{ success: boolean; message: string }>("/api/admin/email/test", {
      method: "POST",
      body: JSON.stringify({ to, useInviteTemplate }),
    }),

  getLogs: () => request<any[]>("/api/admin/email/logs"),
};

// AI Config API
export const aiApi = {
  getConfig: () => request<any>("/api/admin/ai/config"),

  updateConfig: (data: any) =>
    request<any>("/api/admin/ai/config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  test: (prompt?: string) =>
    request<{ success: boolean; message: string; response?: string }>(
      "/api/admin/ai/test",
      {
        method: "POST",
        body: JSON.stringify({ prompt }),
      }
    ),

  getUsage: () => request<any>("/api/admin/ai/usage"),
};

// Bank Gateway API
export const bankGatewayApi = {
  // Configs
  listConfigs: () => request<any[]>("/api/admin/bank-gateway/configs"),

  getConfig: (id: string) =>
    request<any>(`/api/admin/bank-gateway/configs/${id}`),

  createConfig: (data: any) =>
    request<any>("/api/admin/bank-gateway/configs", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateConfig: (id: string, data: any) =>
    request<any>(`/api/admin/bank-gateway/configs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteConfig: (id: string) =>
    request<any>(`/api/admin/bank-gateway/configs/${id}`, {
      method: "DELETE",
    }),

  uploadCertificate: (id: string, data: any) =>
    request<any>(`/api/admin/bank-gateway/configs/${id}/certificate`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  testConfig: (id: string) =>
    request<{ success: boolean; message: string }>(
      `/api/admin/bank-gateway/configs/${id}/test`,
      { method: "POST" }
    ),

  // Accounts
  listAccounts: () => request<any[]>("/api/admin/bank-gateway/accounts"),

  getAccount: (id: string) =>
    request<any>(`/api/admin/bank-gateway/accounts/${id}`),

  createAccount: (data: any) =>
    request<any>("/api/admin/bank-gateway/accounts", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateAccount: (id: string, data: any) =>
    request<any>(`/api/admin/bank-gateway/accounts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteAccount: (id: string) =>
    request<any>(`/api/admin/bank-gateway/accounts/${id}`, {
      method: "DELETE",
    }),

  getAccountBalance: (id: string) =>
    request<any>(`/api/admin/bank-gateway/accounts/${id}/balance`),

  getAccountStatement: (id: string, startDate: string, endDate: string) =>
    request<{
      startDate: string;
      endDate: string;
      openingBalance: number;
      closingBalance: number;
      transactions: Array<{
        id: string;
        date: string;
        description: string;
        amount: number;
        type: "credit" | "debit";
        counterpart?: {
          name?: string;
          document?: string;
          bank?: string;
          agency?: string;
          account?: string;
        };
        pix?: {
          endToEndId?: string;
          txId?: string;
        };
      }>;
    }>(`/api/admin/bank-gateway/accounts/${id}/statement?startDate=${startDate}&endDate=${endDate}`),

  // Logs
  getLogs: (filters?: any) =>
    request<any[]>(
      `/api/admin/bank-gateway/logs${
        filters ? `?${new URLSearchParams(filters).toString()}` : ""
      }`
    ),

  // Dashboard Summary
  getDashboardSummary: () =>
    request<{
      totalBalance: number;
      totalBalanceBRL: string;
      accounts: Array<{
        id: string;
        bankName: string;
        agency: string;
        accountNumber: string;
        balance: number;
        balanceBRL: string;
        lastUpdated: string | null;
      }>;
      lastTransactions: Array<{
        id: string;
        date: string;
        description: string;
        amount: number;
        amountBRL: string;
        type: "credit" | "debit";
        accountName: string;
      }>;
    }>("/api/admin/bank-gateway/dashboard/summary"),
};

// API Keys types
interface ApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  description?: string;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  expiresAt?: string;
  rateLimitPerMinute: number;
  rateLimitPerHour: number;
  ipWhitelist: string[];
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
}

interface ApiKeyCreated extends ApiKey {
  key: string;
  warning: string;
}

interface ApiKeyLog {
  id: string;
  apiKeyId: string;
  endpoint: string;
  method: string;
  ipAddress: string;
  userAgent?: string;
  statusCode: number;
  responseTimeMs: number;
  success: boolean;
  errorMessage?: string;
  createdAt: string;
}

// Company API (area da empresa - self-service)
export const companyApi = {
  // API Keys
  apiKeys: {
    list: () => request<ApiKey[]>("/api/company/api-keys"),

    getById: (id: string) => request<ApiKey>(`/api/company/api-keys/${id}`),

    getLogs: (id: string) => request<ApiKeyLog[]>(`/api/company/api-keys/${id}/logs`),

    create: (data: {
      name: string;
      description?: string;
      expiresAt?: string;
      rateLimitPerMinute?: number;
      rateLimitPerHour?: number;
      ipWhitelist?: string[];
    }) =>
      request<ApiKeyCreated>("/api/company/api-keys", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    revoke: (id: string, reason?: string) =>
      request<void>(`/api/company/api-keys/${id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
  },

  users: {
    list: () => request<any[]>("/api/company/users"),

    invite: (data: { email: string; name: string; role: string }) =>
      request<any>("/api/company/users/invite", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    updateRole: (id: string, role: string) =>
      request<any>(`/api/company/users/${id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      }),

    toggleActive: (id: string) =>
      request<any>(`/api/company/users/${id}/toggle-active`, {
        method: "POST",
      }),
  },

  // Informações da empresa
  getInfo: () =>
    request<{
      id: string;
      cnpj: string;
      razaoSocial: string;
      nomeFantasia: string | null;
    }>("/api/company/info"),

  // Dashboard da empresa
  dashboard: {
    get: () =>
      request<{
        balance: {
          brld: string;
          brldWei: string;
          brl: number;
          yield: number;
          availableBalance: number;
          appliedBalance: number;
          usdcBalance: number;
          usdcLockedBalance: number;
        };
        position: {
          totalPrincipal: number;
          totalCurrent: number;
          yieldAmount: number;
          accumulatedFactor: number;
          businessDays: number;
        };
        product: {
          cdiRate: number;
          annualRate: number;
        };
        wallet: {
          address: string;
          networkId: string;
          networkName: string;
          explorerUrl: string;
        };
        animation: {
          nextBusinessDayAt: string;
          currentDailyRate: number;
          secondsUntilNextBusinessDay: number;
        };
      }>("/api/company/dashboard"),

    getWallet: () =>
      request<{
        address: string;
        networkId: string;
        networkName: string;
        explorerUrl: string;
        balance: string;
        lastSyncedAt?: string;
      }>("/api/company/wallet"),

    getDepositInfo: () =>
      request<{
        bankName: string;
        bankCode: string;
        agency: string;
        accountNumber: string;
        accountDigit?: string;
        pixKey?: string;
        holderName?: string;
        holderDocument?: string;
      }>("/api/company/deposit-info"),

    // Contas bancárias da empresa
    listBankAccounts: () =>
      request<
        Array<{
          id: string;
          bankCode: string;
          bankName: string;
          agency: string;
          accountNumber: string;
          accountDigit?: string;
          accountType: string;
          holderName: string;
          holderDocument: string;
          isVerified: boolean;
          isActive: boolean;
          createdAt: string;
        }>
      >("/api/company/bank-accounts"),

    createBankAccount: (data: {
      bankCode: string;
      bankName: string;
      agency: string;
      accountNumber: string;
      accountDigit?: string;
      accountType: "CHECKING" | "SAVINGS";
      holderName: string;
      holderDocument: string;
    }) =>
      request<any>("/api/company/bank-accounts", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    deleteBankAccount: (id: string) =>
      request<void>(`/api/company/bank-accounts/${id}`, {
        method: "DELETE",
      }),

    // Informações para o modal de resgate
    getWithdrawalInfo: () =>
      request<{
        availableBalance: number;
        availableBalanceFormatted: string;
        blockedBalance: number;
        blockedBalanceFormatted: string;
        totalPrincipal: number;
        totalPrincipalFormatted: string;
        totalYield: number;
        totalYieldFormatted: string;
        minWithdrawalAmount: number;
        minWithdrawalAmountFormatted: string;
      }>("/api/company/withdrawal-info"),

    // Solicitações de saque
    listWithdrawalRequests: () =>
      request<
        Array<{
          id: string;
          amount: number;
          amountFormatted: string;
          status: string;
          bankAccount: {
            bankName: string;
            agency: string;
            accountNumber: string;
            accountDigit?: string;
          };
          requestedAt: string;
          processedAt?: string;
          rejectionReason?: string;
        }>
      >("/api/company/withdrawal-requests"),

    createWithdrawalRequest: (data: {
      amount: number;
      withdrawalType: "ALL" | "PRINCIPAL_ONLY" | "YIELD_ONLY" | "CUSTOM";
      bankAccountId: string;
    }) =>
      request<any>("/api/company/withdrawal-requests", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    cancelWithdrawalRequest: (id: string, reason?: string) =>
      request<void>(`/api/company/withdrawal-requests/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),

    // Operações da empresa (depósitos e saques)
    listOperations: () =>
      request<
        Array<{
          id: string;
          type: "DEPOSIT" | "WITHDRAWAL";
          amount: number;
          amountCents: number;
          status: string;
          description: string;
          txHash: string | null;
          walletAddress: string | null;
          createdAt: string;
          processedAt: string | null;
        }>
      >("/api/company/operations"),

    // Extrato completo (legado)
    getStatement: () =>
      request<{
        entries: Array<{
          id: string;
          date: string;
          type: "DEPOSIT" | "WITHDRAWAL" | "YIELD" | "APPLICATION";
          description: string;
          amount: number;
          balance: number;
          txHash?: string | null;
          status: string;
        }>;
        summary: {
          currentBalance: number;
          totalYield: number;
          totalDeposits: number;
          totalWithdrawals: number;
        };
      }>("/api/company/statement"),

    // Extrato unificado (novo - com filtros)
    getTransactions: (params?: {
      startDate?: string;
      endDate?: string;
      types?: string[];
      currency?: string;
      page?: number;
      limit?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set("startDate", params.startDate);
      if (params?.endDate) searchParams.set("endDate", params.endDate);
      if (params?.types?.length) searchParams.set("types", params.types.join(","));
      if (params?.currency) searchParams.set("currency", params.currency);
      if (params?.page) searchParams.set("page", params.page.toString());
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      const query = searchParams.toString();
      return request<{
        transactions: Array<{
          id: string;
          type: "DEPOSIT" | "WITHDRAWAL" | "APPLICATION" | "REDEMPTION_PRINCIPAL" | "REDEMPTION_YIELD" | "CONVERSION_OUT" | "CONVERSION_IN";
          direction: "CREDIT" | "DEBIT";
          currency: "BRL" | "USDC";
          status: "PENDING" | "COMPLETED" | "FAILED" | "CANCELLED";
          amount: number;
          amountFormatted: string;
          description: string;
          metadata?: Record<string, unknown>;
          createdAt: string;
        }>;
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(`/api/company/transactions${query ? `?${query}` : ""}`);
    },

    // Detalhes da posição com rendimento
    getPositionDetails: () =>
      request<{
        summary: {
          totalPrincipal: number;
          totalCurrent: number;
          totalYield: number;
          yieldPercentage: number;
          positionsCount: {
            total: number;
            active: number;
            inGrace: number;
            redeemed: number;
          };
        };
        positions: Array<{
          id: string;
          principalAmount: number;
          currentAmount: number;
          yieldAmount: number;
          yieldPercentage: number;
          accumulatedFactor: number;
          businessDays: number;
          startDate: string;
          status: string;
        }>;
      }>("/api/company/position"),

    // Histórico de rendimentos diários
    getYieldHistory: () =>
      request<{
        history: Array<{
          date: string;
          dateFormatted: string;
          balanceBrld: number;
          dailyYield: number;
          yieldPercentage: number;
        }>;
        summary: {
          totalYield: number;
          averageDailyYield: number;
          daysCount: number;
          totalPrincipal: number;
        };
      }>("/api/company/yield-history"),

    // Informações para aplicação
    getApplicationInfo: () =>
      request<{
        availableBalance: number;
        availableBalanceFormatted: string;
        appliedBalance: number;
        appliedBalanceFormatted: string;
        accumulatedYield: number;
        accumulatedYieldFormatted: string;
        cdiRate: number;
        annualRate: number;
        monthlyYieldEstimate: number;
        annualYieldEstimate: number;
      }>("/api/company/application-info"),

    // Aplicar BRLD (disponível -> aplicado)
    applyBrld: (amount: number) =>
      request<{
        positionId: string;
        amount: number;
        amountFormatted: string;
        cdiRate: number;
        yieldStartDate: string;
        newAvailableBalance: number;
        newAppliedBalance: number;
      }>("/api/company/apply", {
        method: "POST",
        body: JSON.stringify({ amount }),
      }),

    // Resgatar aplicação (aplicado -> disponível)
    redeemApplication: (amount: number, yieldOnly?: boolean) =>
      request<{
        amount: number;
        amountFormatted: string;
        principalAmount: number;
        yieldAmount: number;
        brlydMintTxHash?: string;
        brlydBurnTxHash?: string;
        newAvailableBalance: number;
        newAppliedBalance: number;
      }>("/api/company/redeem-application", {
        method: "POST",
        body: JSON.stringify({ amount, yieldOnly }),
      }),

    // Extrato PDF
    getExtratoPdf: async (startDate?: string, endDate?: string): Promise<Blob> => {
      const token = useAuthStore.getState().accessToken;
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      const response = await fetch(`${API_URL}/api/company/extrato-pdf${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Erro ao baixar extrato");
      return response.blob();
    },
  },

  // Conversão de moedas (BRLD <-> USDC)
  conversion: {
    // Informações USDC
    getUsdcInfo: () =>
      request<{
        balance: number;
        balanceFormatted: string;
        balanceUsd: string;
        equivalentBrl: number;
        equivalentBrlFormatted: string;
        currentRate: number;
      }>("/api/company/conversion/usdc"),

    // Cotação atual
    getExchangeRate: () =>
      request<{
        rate: number;
        spread: number;
        bidRate: number;
        askRate: number;
        provider: string;
        updatedAt: string;
      }>("/api/company/conversion/exchange-rate"),

    // Obter cotação
    getQuote: (fromCurrency: "BRL" | "USDC", toCurrency: "BRL" | "USDC", amount: number) =>
      request<{
        quoteId: string;
        fromCurrency: string;
        toCurrency: string;
        fromAmount: number;
        toAmount: number;
        rate: number;
        spread: number;
        expiresAt: string;
        provider: string;
      }>("/api/company/conversion/quote", {
        method: "POST",
        body: JSON.stringify({ fromCurrency, toCurrency, amount }),
      }),

    // Executar conversão
    convert: (quoteId: string) =>
      request<{
        conversionId: string;
        fromCurrency: string;
        toCurrency: string;
        fromAmount: number;
        toAmount: number;
        exchangeRate: number;
        status: string;
        newBrldBalance: number;
        newUsdcBalance: number;
        burnTxHash?: string;
        mintTxHash?: string;
        tradeId?: string;
        tradeStatus?: string;
      }>("/api/company/conversion/convert", {
        method: "POST",
        body: JSON.stringify({ quoteId }),
      }),

    // Histórico de conversões
    listConversions: () =>
      request<Array<{
        id: string;
        fromCurrency: string;
        toCurrency: string;
        fromAmount: number;
        toAmount: number;
        rate: number;
        status: string;
        createdAt: string;
        tradeId?: string;
        tradeStatus?: string;
        tradeCainvestStatus?: string;
        tradeReconStatus?: string;
      }>>("/api/company/conversion/conversions"),

    // Status da trade de uma conversão
    getTradeStatus: (conversionId: string) =>
      request<{
        tradeId: string;
        status: string;
        cainvestStatus?: string;
        reconciliationStatus?: string;
        settledAt?: string;
        completedAt?: string;
        errorMessage?: string;
        createdAt: string;
      }>(`/api/company/conversion/conversions/${conversionId}/trade-status`),
  },
};

// CDI Rates API
export const cdiApi = {
  list: (limit?: number) =>
    request<any[]>(`/api/admin/cdi${limit ? `?limit=${limit}` : ""}`),

  getCurrent: () => request<any>("/api/admin/cdi/current"),

  getById: (id: string) => request<any>(`/api/admin/cdi/${id}`),

  create: (data: { referenceDate: string; rate: number }) =>
    request<any>("/api/admin/cdi", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { rate: number }) =>
    request<any>(`/api/admin/cdi/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/cdi/${id}`, {
      method: "DELETE",
    }),

  // Sincronizacao automatica
  sync: () =>
    request<{
      success: boolean;
      message: string;
      imported: number;
      skipped: number;
      lastRate?: { date: string; rate: number };
    }>("/api/admin/cdi/sync", {
      method: "POST",
    }),

  getSchedulerStatus: () =>
    request<{
      nextRun: string;
      schedule: string;
      timezone: string;
      description: string;
    }>("/api/admin/cdi/scheduler/status"),
};

// Holidays API
export const holidaysApi = {
  list: () => request<any[]>("/api/admin/holidays"),

  getByYear: (year: number) => request<any[]>(`/api/admin/holidays/year/${year}`),

  getById: (id: string) => request<any>(`/api/admin/holidays/${id}`),

  create: (data: { date: string; description: string }) =>
    request<any>("/api/admin/holidays", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  import: (holidays: { date: string; description: string }[]) =>
    request<{ imported: number; skipped: number; errors: string[] }>(
      "/api/admin/holidays/import",
      {
        method: "POST",
        body: JSON.stringify({ holidays }),
      }
    ),

  delete: (id: string) =>
    request<any>(`/api/admin/holidays/${id}`, {
      method: "DELETE",
    }),
};

// Products API
export const productsApi = {
  list: () => request<any[]>("/api/admin/products"),

  listActive: () => request<any[]>("/api/admin/products/active"),

  getSummary: () =>
    request<{
      products: {
        id: string;
        name: string;
        cdiRate: string;
        gracePeriodDays: number;
        isDefault: boolean;
        volume: number;
        positionsCount: number;
      }[];
      totalVolume: number;
      totalProducts: number;
    }>("/api/admin/products/summary"),

  getDefault: () => request<any>("/api/admin/products/default"),

  getById: (id: string) => request<any>(`/api/admin/products/${id}`),

  create: (data: {
    name: string;
    description?: string;
    cdiRate: number;
    gracePeriodDays?: number;
    dayBasis?: "BUSINESS_252" | "CALENDAR_365";
    isDefault?: boolean;
  }) =>
    request<any>("/api/admin/products", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  setDefault: (id: string) =>
    request<any>(`/api/admin/products/${id}/set-default`, {
      method: "POST",
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/products/${id}`, {
      method: "DELETE",
    }),
};

// Positions API
export const positionsApi = {
  list: () => request<any[]>("/api/admin/positions"),

  getByCompany: (companyId: string) =>
    request<any[]>(`/api/admin/positions/company/${companyId}`),

  getSummaryByCompany: (companyId: string) =>
    request<any>(`/api/admin/positions/company/${companyId}/summary`),

  getGlobalSummary: () =>
    request<{
      totalPrincipalCents: number;
      totalCurrentCents: number;
      totalYieldCents: number;
      yieldPercentage: number;
      positionsCount: { total: number; active: number; inGrace: number };
    }>("/api/admin/positions/summary"),

  getById: (id: string) => request<any>(`/api/admin/positions/${id}`),

  getYield: (id: string) => request<any>(`/api/admin/positions/${id}/yield`),

  create: (data: { companyId: string; productId?: string; amountCents: number }) =>
    request<any>("/api/admin/positions", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  redeem: (id: string) =>
    request<any>(`/api/admin/positions/${id}/redeem`, {
      method: "POST",
    }),

  getExtratoPdf: async (companyId: string, startDate?: string, endDate?: string): Promise<Blob> => {
    const token = useAuthStore.getState().accessToken;
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    const qs = params.toString();
    const response = await fetch(`${API_URL}/api/admin/positions/company/${companyId}/extrato-pdf${qs ? `?${qs}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Erro ao baixar extrato");
    return response.blob();
  },
};

// Blockchain Networks API
export const blockchainNetworksApi = {
  list: () => request<any[]>("/api/admin/blockchain-networks"),

  getById: (id: string) => request<any>(`/api/admin/blockchain-networks/${id}`),

  getDefault: () => request<any>("/api/admin/blockchain-networks/default"),

  create: (data: {
    id: string;
    name: string;
    rpcUrl: string;
    chainId: string;
    type: "MAINNET" | "TESTNET" | "LOCAL";
    explorerUrl?: string;
    currencySymbol?: string;
    isDefault?: boolean;
  }) =>
    request<any>("/api/admin/blockchain-networks", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/blockchain-networks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/blockchain-networks/${id}`, {
      method: "DELETE",
    }),

  setDefault: (id: string) =>
    request<any>(`/api/admin/blockchain-networks/${id}/set-default`, {
      method: "POST",
    }),

  testConnection: (id: string) =>
    request<{ success: boolean; blockNumber?: number; latency?: number; error?: string }>(
      `/api/admin/blockchain-networks/${id}/test`,
      { method: "POST" }
    ),
};

// Wallets API
export const walletsApi = {
  list: (filters?: { companyId?: string; networkId?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.networkId) params.append("networkId", filters.networkId);
    if (filters?.status) params.append("status", filters.status);
    const query = params.toString();
    return request<any[]>(`/api/admin/wallets${query ? `?${query}` : ""}`);
  },

  getById: (id: string) => request<any>(`/api/admin/wallets/${id}`),

  create: (data: {
    companyId: string;
    networkId: string;
    label?: string;
    purpose?: "TREASURY" | "OPERATIONS" | "COLLECTION" | "ADMIN_MINTER";
    isDefault?: boolean;
  }) =>
    request<any>("/api/admin/wallets", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  import: (data: {
    companyId: string;
    networkId: string;
    privateKey?: string;
    mnemonic?: string;
    derivationPath?: string;
    label?: string;
    purpose?: "TREASURY" | "OPERATIONS" | "COLLECTION" | "ADMIN_MINTER";
    isDefault?: boolean;
  }) =>
    request<any>("/api/admin/wallets/import", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/wallets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/wallets/${id}`, {
      method: "DELETE",
    }),

  setDefault: (id: string) =>
    request<any>(`/api/admin/wallets/${id}/set-default`, {
      method: "POST",
    }),

  syncBalance: (id: string) =>
    request<any>(`/api/admin/wallets/${id}/sync`, {
      method: "POST",
    }),

  getBalance: (id: string) =>
    request<{ balance: string; balanceFormatted: string; nonce: number; syncedAt: string }>(
      `/api/admin/wallets/${id}/balance`
    ),
};

// Stablecoin Contract API
export const stablecoinContractApi = {
  list: () => request<any[]>("/api/admin/stablecoin-contract/configs"),

  getById: (id: string) => request<any>(`/api/admin/stablecoin-contract/configs/${id}`),

  getActive: () => request<any>("/api/admin/stablecoin-contract/configs/active"),

  create: (data: {
    id: string;
    name: string;
    address: string;
    networkId: string;
    abi: any[];
    isActive?: boolean;
  }) =>
    request<any>("/api/admin/stablecoin-contract/configs", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/stablecoin-contract/configs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<any>(`/api/admin/stablecoin-contract/configs/${id}`, {
      method: "DELETE",
    }),

  test: (id: string) =>
    request<{
      success: boolean;
      name?: string;
      symbol?: string;
      decimals?: number;
      totalSupply?: string;
      owner?: string;
      latency?: number;
      error?: string;
    }>(`/api/admin/stablecoin-contract/configs/${id}/test`, { method: "POST" }),

  activate: (id: string) =>
    request<any>(`/api/admin/stablecoin-contract/configs/${id}/activate`, {
      method: "POST",
    }),

  getInfo: () => request<any>("/api/admin/stablecoin-contract/info"),

  getEvents: (limit?: number) =>
    request<{ events: any[]; total: number; fromBlock: number; toBlock: number }>(
      `/api/admin/stablecoin-contract/events${limit ? `?limit=${limit}` : ""}`
    ),

  // Endpoints para contrato especifico por ID
  getInfoById: (id: string) =>
    request<any>(`/api/admin/stablecoin-contract/configs/${id}/info`),

  getEventsById: (id: string, limit?: number) =>
    request<{ events: any[]; total: number; fromBlock: number; toBlock: number }>(
      `/api/admin/stablecoin-contract/configs/${id}/events${limit ? `?limit=${limit}` : ""}`
    ),

  // Status do contrato
  getStatus: (id: string) =>
    request<{
      isPaused: boolean;
      totalSupply: string;
      totalSupplyFormatted: string;
      minterRole: string;
      pauserRole: string;
      complianceRole: string;
      adminRole: string;
    }>(`/api/admin/stablecoin-contract/configs/${id}/status`),

  // Verificar blacklist
  checkBlacklist: (id: string, address: string) =>
    request<{ address: string; isBlacklisted: boolean }>(
      `/api/admin/stablecoin-contract/configs/${id}/blacklist/${address}`
    ),

  // Verificar roles de um endereco
  checkRoles: (id: string, address: string) =>
    request<{
      address: string;
      isMinter: boolean;
      isPauser: boolean;
      isComplianceOfficer: boolean;
      isAdmin: boolean;
    }>(`/api/admin/stablecoin-contract/configs/${id}/roles/${address}`),

  // Acoes administrativas
  pause: (id: string) =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/pause`,
      { method: "POST" }
    ),

  unpause: (id: string) =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/unpause`,
      { method: "POST" }
    ),

  addToBlacklist: (id: string, address: string) =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/blacklist/add`,
      { method: "POST", body: JSON.stringify({ address }) }
    ),

  removeFromBlacklist: (id: string, address: string) =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/blacklist/remove`,
      { method: "POST", body: JSON.stringify({ address }) }
    ),

  clawback: (id: string, from: string, to: string, amount: string) =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/clawback`,
      { method: "POST", body: JSON.stringify({ from, to, amount }) }
    ),

  grantRole: (id: string, address: string, role: "MINTER" | "PAUSER" | "COMPLIANCE_OFFICER") =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/roles/grant`,
      { method: "POST", body: JSON.stringify({ address, role }) }
    ),

  revokeRole: (id: string, address: string, role: "MINTER" | "PAUSER" | "COMPLIANCE_OFFICER") =>
    request<{ success: boolean; transactionHash?: string; explorerUrl?: string; error?: string }>(
      `/api/admin/stablecoin-contract/configs/${id}/roles/revoke`,
      { method: "POST", body: JSON.stringify({ address, role }) }
    ),
};

// Jobs API
export const jobsApi = {
  list: () => request<any[]>("/api/admin/jobs"),

  getStats: () => request<any>("/api/admin/jobs/stats"),

  getByName: (name: string) => request<any>(`/api/admin/jobs/${name}`),

  getExecutions: (jobName?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (jobName) params.append("jobName", jobName);
    if (limit) params.append("limit", limit.toString());
    const query = params.toString();
    return request<any[]>(`/api/admin/jobs/executions${query ? `?${query}` : ""}`);
  },

  getLatestExecutions: () => request<any>("/api/admin/jobs/executions/latest"),

  getJobExecutions: (name: string, limit?: number) =>
    request<any[]>(`/api/admin/jobs/${name}/executions${limit ? `?limit=${limit}` : ""}`),

  toggleJob: (name: string, enabled: boolean) =>
    request<any>(`/api/admin/jobs/${name}/toggle?enabled=${enabled}`, {
      method: "POST",
    }),

  runJob: (name: string) =>
    request<{ success: boolean; message: string; details?: any }>(
      `/api/admin/jobs/${name}/run`,
      { method: "POST" }
    ),
};

// Deposits API
export const depositsApi = {
  list: (filters?: { status?: string; companyId?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());
    const query = params.toString();
    return request<{
      deposits: Array<{
        id: string;
        endToEndId: string;
        txId?: string;
        payerDocument: string;
        payerName?: string;
        payerBank?: string;
        amountCents: number;
        pixDate: string;
        pixDescription?: string;
        status: "PENDING" | "PROCESSING" | "MINTED" | "FAILED" | "UNMATCHED";
        companyId?: string;
        mintTxHash?: string;
        mintedAt?: string;
        walletAddress?: string;
        errorMessage?: string;
        retryCount: number;
        createdAt: string;
      }>;
      total: number;
    }>(`/api/admin/deposits${query ? `?${query}` : ""}`);
  },

  getById: (id: string) =>
    request<any>(`/api/admin/deposits/${id}`),

  getStats: () =>
    request<{
      total: number;
      pending: number;
      processing: number;
      minted: number;
      failed: number;
      unmatched: number;
      totalMintedBRL: number;
    }>("/api/admin/deposits/stats"),

  reprocess: (id: string) =>
    request<{ success: boolean; message: string }>(`/api/admin/deposits/${id}/reprocess`, {
      method: "POST",
    }),

  triggerProcessing: () =>
    request<{
      success: boolean;
      message: string;
      stats?: {
        newDepositsCreated: number;
        depositsProcessed: number;
        depositsMinted: number;
        depositsFailed: number;
        depositsUnmatched: number;
      };
    }>("/api/admin/deposits/process", {
      method: "POST",
    }),

  getMinterStatus: () =>
    request<{
      minterAddress: string | null;
      hasMinterRole: boolean;
      roleError?: string;
      balance: string;
      balanceError?: string;
      isReady: boolean;
    }>("/api/admin/deposits/system/minter-status"),
};

// Operations API (nova arquitetura)
export const operationsApi = {
  listStatements: (filters?: {
    status?: string;
    movementType?: string;
    category?: string;
    actionType?: string;
    companyId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.movementType) params.append("movementType", filters.movementType);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.actionType) params.append("actionType", filters.actionType);
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());
    const query = params.toString();
    return request<{
      statements: Array<{
        id: string;
        bankEventId: string;
        bankAccountId: string;
        movementType: "CREDIT" | "DEBIT";
        category: string;
        amountCents: number;
        transactionDate: string;
        description?: string;
        counterpartName?: string;
        counterpartDocument?: string;
        counterpartBank?: string;
        counterpartAgency?: string;
        counterpartAccount?: string;
        pixEndToEndId?: string;
        analyzed: boolean;
        analyzedAt?: string;
        actionType?: string;
        status: string;
        matchedCompanyId?: string;
        matchMethod?: string;
        matchConfidence?: number;
        depositId?: string;
        withdrawalId?: string;
        applicationId?: string;
        errorMessage?: string;
        createdAt: string;
      }>;
      total: number;
    }>(`/api/admin/operations/statements${query ? `?${query}` : ""}`);
  },

  getStats: () =>
    request<{
      total: number;
      byStatus: Record<string, number>;
      byMovementType: Record<string, number>;
      byCategory: Record<string, number>;
      byAction: Record<string, number>;
      pendingMint: number;
      pendingBurn: number;
      pendingManual: number;
    }>("/api/admin/operations/stats"),

  triggerAnalysis: () =>
    request<{
      imported: number;
      analyzed: number;
      byCategory: Record<string, number>;
      byAction: Record<string, number>;
    }>("/api/admin/operations/analyze", {
      method: "POST",
    }),

  manualMatch: (statementId: string, companyId: string) =>
    request<{ success: boolean }>(`/api/admin/operations/statements/${statementId}/match`, {
      method: "PATCH",
      body: JSON.stringify({ companyId }),
    }),

  ignoreStatement: (statementId: string, reason?: string) =>
    request<{ success: boolean }>(`/api/admin/operations/statements/${statementId}/ignore`, {
      method: "PATCH",
      body: JSON.stringify({ reason }),
    }),

  getApplicationsSummary: () =>
    request<{
      totalAppliedCents: number;
      totalAppliedBRL: string;
      totalRedeemedCents: number;
      totalRedeemedBRL: string;
      currentBalanceCents: number;
      currentBalanceBRL: string;
      byType: Record<string, { count: number; totalCents: number }>;
      recentApplications: Array<{
        id: string;
        type: string;
        productName: string | null;
        principalCents: number;
        purchaseDate: string;
        status: string;
      }>;
    }>("/api/admin/operations/applications/summary"),
};

// Applications API (módulo de aplicações financeiras)
export const applicationsApi = {
  // Dashboard
  getDashboard: () =>
    request<{
      totalVolumeCents: number;
      totalCurrentValueCents: number;
      totalEarningsCents: number;
      monthlyEarningsCents: number;
      projectedMonthlyEarningsCents: number;
      applicationCount: number;
      activeCount: number;
      needsConfigCount: number;
      byType: { type: string; volumeCents: number; count: number }[];
      byStatus: { status: string; count: number }[];
      recentEarnings: { date: string; earningsCents: number }[];
      fifoSchedule: { month: string; maturingCents: number; byType: Record<string, number> }[];
    }>("/api/admin/applications/dashboard"),

  getEarningsHistory: (months?: number) =>
    request<{ month: string; earningsCents: number }[]>(
      `/api/admin/applications/earnings-history${months ? `?months=${months}` : ""}`
    ),

  getYieldSummary: () =>
    request<{
      totalPrincipalCents: number;
      totalCurrentCents: number;
      totalYieldCents: number;
      applicationsCount: number;
    }>("/api/admin/applications/yield-summary"),

  calculateEarnings: () =>
    request<{ processed: number; totalEarnings: number }>(
      "/api/admin/applications/calculate-earnings",
      { method: "POST" }
    ),

  // Aplicações
  list: (filters?: {
    status?: string;
    applicationType?: string;
    bankAccountId?: string;
    needsRateConfig?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.applicationType) params.append("applicationType", filters.applicationType);
    if (filters?.bankAccountId) params.append("bankAccountId", filters.bankAccountId);
    if (filters?.needsRateConfig) params.append("needsRateConfig", "true");
    const query = params.toString();
    return request<any[]>(`/api/admin/applications${query ? `?${query}` : ""}`);
  },

  getNeedsConfig: () => request<any[]>("/api/admin/applications/needs-config"),

  getById: (id: string) => request<any>(`/api/admin/applications/${id}`),

  update: (id: string, data: any) =>
    request<any>(`/api/admin/applications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  applyTemplate: (id: string, templateId: string) =>
    request<any>(`/api/admin/applications/${id}/apply-template`, {
      method: "POST",
      body: JSON.stringify({ templateId }),
    }),

  redeem: (id: string, data: { amountCents: number; redemptionDate: string; description?: string }) =>
    request<any>(`/api/admin/applications/${id}/redeem`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createTemplateFromApplication: (
    id: string,
    data: {
      descriptionPattern: string;
      rateType: string;
      rateValue: string;
      productName?: string;
      liquidityDays?: number;
    }
  ) =>
    request<any>(`/api/admin/applications/${id}/create-template`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Templates
  listTemplates: () => request<any[]>("/api/admin/applications/templates/list"),

  getTemplate: (id: string) => request<any>(`/api/admin/applications/templates/${id}`),

  createTemplate: (data: {
    descriptionPattern: string;
    patternType?: string;
    applicationType: string;
    productName?: string;
    rateType: string;
    rateValue: string;
    liquidityDays?: number;
    hasMaturity?: boolean;
    defaultMaturityDays?: number;
    priority?: number;
    notes?: string;
  }) =>
    request<any>("/api/admin/applications/templates", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateTemplate: (id: string, data: any) =>
    request<any>(`/api/admin/applications/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteTemplate: (id: string) =>
    request<void>(`/api/admin/applications/templates/${id}`, {
      method: "DELETE",
    }),
};

// Iniciador API (Open Finance config)
export const iniciadorApi = {
  getConfig: () =>
    request<{
      id?: string;
      environment?: "SANDBOX" | "PRODUCTION";
      clientId?: string;
      clientSecretMasked?: string;
      dataBaseUrl?: string;
      linksBaseUrl?: string;
      redirectUrl?: string | null;
      defaultPermissions?: string[];
      webhookUrl?: string | null;
      isActive?: boolean;
      lastTestedAt?: string | null;
      lastTestStatus?: string | null;
      lastTestMessage?: string | null;
      createdAt?: string;
      updatedAt?: string;
      configured?: boolean;
    }>("/api/admin/iniciador/config"),

  createConfig: (data: {
    environment: "SANDBOX" | "PRODUCTION";
    clientId: string;
    clientSecret: string;
    dataBaseUrl?: string;
    linksBaseUrl?: string;
    redirectUrl?: string;
    defaultPermissions?: string[];
    webhookUrl?: string;
    isActive?: boolean;
  }) =>
    request<any>("/api/admin/iniciador/config", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateConfig: (data: {
    environment?: "SANDBOX" | "PRODUCTION";
    clientId?: string;
    clientSecret?: string;
    dataBaseUrl?: string;
    linksBaseUrl?: string;
    redirectUrl?: string;
    defaultPermissions?: string[];
    webhookUrl?: string;
    isActive?: boolean;
  }) =>
    request<any>("/api/admin/iniciador/config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  testConnection: () =>
    request<{
      success: boolean;
      message: string;
      expiresIn?: number;
      error?: string;
    }>("/api/admin/iniciador/config/test", {
      method: "POST",
    }),
};

// Open Finance API
export const openFinanceApi = {
  // Links
  getLinks: () =>
    request<Array<{
      id: string;
      companyId: string;
      externalId: string;
      iniciadorLinkId: string | null;
      status: string;
      statusMessage: string | null;
      participantId: string | null;
      participantName: string | null;
      participantLogo: string | null;
      permissions: string[] | null;
      interfaceUrl: string | null;
      consentRedirectUrl: string | null;
      createdAt: string;
      authorizedAt: string | null;
      expiresAt: string | null;
      revokedAt: string | null;
      lastSyncAt: string | null;
    }>>("/api/open-finance/links"),

  createLink: (permissions?: string[]) =>
    request<{
      id: string;
      authorizationUrl: string;
      status: string;
    }>("/api/open-finance/links", {
      method: "POST",
      body: JSON.stringify({ permissions }),
    }),

  revokeLink: (linkId: string) =>
    request<any>(`/api/open-finance/links/${linkId}`, {
      method: "DELETE",
    }),

  // Accounts
  getAccounts: () =>
    request<Array<{
      id: string;
      linkId: string;
      companyId: string;
      accountId: string;
      brandName: string | null;
      companyCnpj: string | null;
      accountType: string | null;
      compeCode: string | null;
      branchCode: string | null;
      accountNumber: string | null;
      checkDigit: string | null;
      cachedAvailableBalance: string | null;
      cachedBlockedBalance: string | null;
      cachedBalanceAt: string | null;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>>("/api/open-finance/accounts"),

  refreshAccountBalance: (accountId: string) =>
    request<any>(`/api/open-finance/accounts/${accountId}/refresh`, {
      method: "POST",
    }),

  // Summary
  getSummary: () =>
    request<{
      totalAvailable: string;
      totalBlocked: string;
      accountsCount: number;
      activeLinksCount: number;
      lastSyncAt: string | null;
    }>("/api/open-finance/summary"),

  // Participants
  getParticipants: () =>
    request<Array<{
      id: string;
      tradeName: string;
      avatar: string;
      isOutage: boolean;
    }>>("/api/open-finance/participants"),
};

// Admin Withdrawals API
export const adminWithdrawalsApi = {
  list: (filters?: {
    status?: string;
    companySearch?: string;
    page?: number;
    limit?: number;
    sortBy?: "createdAt" | "amount";
    sortOrder?: "asc" | "desc";
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.companySearch) params.append("companySearch", filters.companySearch);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    const query = params.toString();
    return request<{
      data: Array<{
        id: string;
        companyName: string;
        amount: number;
        amountFormatted: string;
        principalAmount?: number;
        yieldAmount?: number;
        status: string;
        bankAccount: string;
        requestedAt: string;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/api/admin/withdrawals${query ? `?${query}` : ""}`);
  },

  getById: (id: string) =>
    request<{
      id: string;
      companyId: string;
      companyName: string;
      companyCnpj: string;
      amount: number;
      amountFormatted: string;
      principalAmount?: number;
      principalAmountFormatted?: string;
      yieldAmount?: number;
      yieldAmountFormatted?: string;
      withdrawalType: string;
      status: string;
      bankAccount: {
        bankName: string;
        agency: string;
        accountNumber: string;
        accountDigit?: string;
        holderName: string;
        pixKey?: string;
      };
      requestedAt: string;
      requestedByUser?: string;
      processingStartedAt?: string;
      processingCompletedAt?: string;
      paidAt?: string;
      burnBrldTxHash?: string;
      mintBrldyTxHash?: string;
      burnBrldyTxHash?: string;
      bankTransferTxId?: string;
      walletAddress?: string;
      errorMessage?: string;
      cancellationReason?: string;
      createdAt: string;
    }>(`/api/admin/withdrawals/${id}`),

  getStats: () =>
    request<{
      total: number;
      pending: number;
      processing: number;
      completed: number;
      failed: number;
      cancelled: number;
      totalAmountPending: number;
      totalAmountCompleted: number;
      bankBalance?: number;
    }>("/api/admin/withdrawals/stats"),

  retry: (id: string) =>
    request<{ success: boolean; message: string }>(`/api/admin/withdrawals/${id}/retry`, {
      method: "POST",
    }),

  cancel: (id: string, reason?: string) =>
    request<{ success: boolean; message: string }>(`/api/admin/withdrawals/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
};

// USDC Trades API (Admin)
export const usdcTradesApi = {
  list: (filters?: {
    status?: string;
    type?: string;
    companyId?: string;
    providerCode?: string;
    reconciliationStatus?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.type) params.append("type", filters.type);
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.providerCode) params.append("providerCode", filters.providerCode);
    if (filters?.reconciliationStatus) params.append("reconciliationStatus", filters.reconciliationStatus);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const query = params.toString();
    return request<{
      items: Array<{
        id: string;
        currencyConversionId: string | null;
        companyId: string;
        companyName?: string;
        cainvestTradeId?: string;
        cainvestQuoteId?: string;
        providerCode?: string;
        providerTradeId?: string;
        providerQuoteId?: string;
        providerStatus?: string;
        stablecoin: string;
        type: "BUY" | "SELL";
        brlAmount: number;
        usdcAmount: number;
        rate: string;
        status: string;
        cainvestStatus: string | null;
        settledAt: string | null;
        completedAt: string | null;
        reconciledAt: string | null;
        reconciliationStatus: string | null;
        reconciliationNotes: string | null;
        burnTxHash: string | null;
        mintTxHash: string | null;
        retryCount: number;
        lastCheckAt: string | null;
        nextCheckAt: string | null;
        errorMessage: string | null;
        createdAt: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/usdc-trades${query ? `?${query}` : ""}`);
  },

  getById: (id: string) =>
    request<{
      id: string;
      currencyConversionId: string | null;
      companyId: string;
      companyName?: string;
      cainvestTradeId?: string;
      cainvestQuoteId?: string;
      providerCode?: string;
      providerTradeId?: string;
      providerQuoteId?: string;
      providerStatus?: string;
      stablecoin: string;
      type: "BUY" | "SELL";
      brlAmount: number;
      usdcAmount: number;
      rate: string;
      status: string;
      cainvestStatus: string | null;
      settledAt: string | null;
      completedAt: string | null;
      reconciledAt: string | null;
      reconciliationStatus: string | null;
      reconciliationNotes: string | null;
      burnTxHash: string | null;
      mintTxHash: string | null;
      retryCount: number;
      lastCheckAt: string | null;
      nextCheckAt: string | null;
      errorMessage: string | null;
      createdAt: string;
      updatedAt: string;
    }>(`/api/admin/usdc-trades/${id}`),

  getStats: () =>
    request<{
      total: number;
      pending: number;
      processing: number;
      settled: number;
      completed: number;
      failed: number;
      expired: number;
      todayBrlVolume: number;
      todayBrlVolumeFormatted: string;
      todayUsdcVolume: number;
      todayUsdcVolumeFormatted: string;
      divergent: number;
    }>("/api/admin/usdc-trades/stats"),

  syncTrade: (id: string) =>
    request<{
      success: boolean;
      error?: string;
      previousStatus?: string;
      newStatus?: string;
      cainvestStatus?: string;
    }>(`/api/admin/usdc-trades/${id}/sync`, { method: "POST" }),

  syncAll: () =>
    request<{
      totalProcessed: number;
      updated: number;
      completed: number;
      failed: number;
      errors: string[];
    }>("/api/admin/usdc-trades/sync-all", { method: "POST" }),

  reconcile: (id: string, data: { status: string; notes?: string }) =>
    request<any>(`/api/admin/usdc-trades/${id}/reconcile`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  complete: (id: string) =>
    request<any>(`/api/admin/usdc-trades/${id}/complete`, { method: "POST" }),

  fail: (id: string, reason: string) =>
    request<any>(`/api/admin/usdc-trades/${id}/fail`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
};

// CAInvest Account API (Admin)
export const cainvestAccountApi = {
  getSummary: () =>
    request<{
      brlBalance: number;
      brlBalanceFormatted: string;
      usdcBalance: number;
      usdcBalanceFormatted: string;
      alerts: {
        lowBrlBalance: boolean;
        lowUsdcBalance: boolean;
      };
      lastSyncAt?: string;
      syncStatus?: string;
      lastSyncBrlBalance?: number;
      lastSyncUsdcBalance?: number;
    }>("/api/admin/cainvest-account/summary"),

  getMovements: (filters?: {
    type?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const query = params.toString();
    return request<{
      items: Array<{
        id: string;
        type: string;
        brlAmount: number;
        usdcAmount: number;
        brlBalanceAfter: number;
        usdcBalanceAfter: number;
        externalReference?: string;
        description?: string;
        currencyConversionId?: string;
        createdAt: string;
        createdByName?: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/cainvest-account/movements${query ? `?${query}` : ""}`);
  },

  recordDeposit: (data: { brlAmount?: number; usdcAmount?: number; description?: string }) =>
    request<any>("/api/admin/cainvest-account/deposit", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  recordWithdrawal: (data: { brlAmount?: number; usdcAmount?: number; description?: string }) =>
    request<any>("/api/admin/cainvest-account/withdrawal", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  recordAdjustment: (data: { brlAmount?: number; usdcAmount?: number; description: string }) =>
    request<any>("/api/admin/cainvest-account/adjustment", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  syncWithApi: () =>
    request<{
      success: boolean;
      error?: string;
      apiBrlBalance?: number;
      apiUsdcBalance?: number;
      status?: string;
    }>("/api/admin/cainvest-account/sync", { method: "POST" }),

  getConfig: () =>
    request<{
      minBrlBalance: number;
      minUsdcBalance: number;
      alertEmail?: string;
    }>("/api/admin/cainvest-account/config"),

  updateConfig: (data: {
    minBrlBalance?: number;
    minUsdcBalance?: number;
    alertEmail?: string;
  }) =>
    request<any>("/api/admin/cainvest-account/config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// Liquidity Provider API (Admin)
export const liquidityProviderApi = {
  // Configurações
  listConfigs: () =>
    request<Array<{
      id: string;
      providerCode: string;
      name: string;
      baseUrl: string;
      authUrl: string | null;
      organizationId: string | null;
      environment: string;
      defaultSpreadPercentage: string | null;
      minOrderAmountCents: string | null;
      maxOrderAmountCents: string | null;
      isActive: boolean | null;
      lastTestedAt: string | null;
      lastTestStatus: string | null;
      lastTestError: string | null;
      createdAt: string;
      updatedAt: string;
      hasApiKey: boolean;
      hasApiSecret: boolean;
    }>>("/api/admin/liquidity-provider/configs"),

  getConfig: (id: string) =>
    request<{
      id: string;
      providerCode: string;
      name: string;
      baseUrl: string;
      authUrl: string | null;
      organizationId: string | null;
      environment: string;
      defaultSpreadPercentage: string | null;
      minOrderAmountCents: string | null;
      maxOrderAmountCents: string | null;
      isActive: boolean | null;
      lastTestedAt: string | null;
      lastTestStatus: string | null;
      lastTestError: string | null;
      createdAt: string;
      updatedAt: string;
      hasApiKey: boolean;
      hasApiSecret: boolean;
    }>(`/api/admin/liquidity-provider/configs/${id}`),

  getConfigByCode: (code: string) =>
    request<{
      id: string;
      providerCode: string;
      name: string;
      baseUrl: string;
      authUrl: string | null;
      organizationId: string | null;
      environment: string;
      defaultSpreadPercentage: string | null;
      minOrderAmountCents: string | null;
      maxOrderAmountCents: string | null;
      isActive: boolean | null;
      lastTestedAt: string | null;
      lastTestStatus: string | null;
      lastTestError: string | null;
      createdAt: string;
      updatedAt: string;
      hasApiKey: boolean;
      hasApiSecret: boolean;
    } | null>(`/api/admin/liquidity-provider/configs/code/${code}`),

  upsertConfig: (data: {
    providerCode: string;
    name: string;
    apiKey?: string;
    apiSecret?: string;
    baseUrl: string;
    authUrl?: string;
    organizationId?: string;
    environment: "sandbox" | "production";
    defaultSpreadPercentage?: string;
    minOrderAmountCents?: string;
    maxOrderAmountCents?: string;
    isActive?: boolean;
  }) =>
    request<any>("/api/admin/liquidity-provider/configs", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  setActive: (id: string, isActive: boolean) =>
    request<{ success: boolean }>(`/api/admin/liquidity-provider/configs/${id}/active`, {
      method: "PUT",
      body: JSON.stringify({ isActive }),
    }),

  deleteConfig: (id: string) =>
    request<{ success: boolean }>(`/api/admin/liquidity-provider/configs/${id}`, {
      method: "DELETE",
    }),

  testConnection: (code: string) =>
    request<{ success: boolean; message: string; data?: unknown }>(
      `/api/admin/liquidity-provider/configs/${code}/test`,
      { method: "POST" }
    ),

  getBalance: (code: string) =>
    request<{ brl: number; usdc: number } | null>(
      `/api/admin/liquidity-provider/configs/${code}/balance`
    ),

  // Status
  getStatus: () =>
    request<{
      providerName: string;
      isRealProvider: boolean;
    }>("/api/admin/liquidity-provider/status"),

  reload: () =>
    request<{
      providerName: string;
      isRealProvider: boolean;
    }>("/api/admin/liquidity-provider/reload", { method: "POST" }),

  // Logs
  listLogs: (options?: { configId?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.configId) params.append("configId", options.configId);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    const query = params.toString();
    return request<{ logs: unknown[]; total: number }>(
      `/api/admin/liquidity-provider/logs${query ? `?${query}` : ""}`
    );
  },

  // Rules (regras de prioridade/fallback)
  listRules: () =>
    request<Array<{
      id: string;
      stablecoin: string;
      providerCode: string;
      priority: number;
      role: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>>("/api/admin/liquidity-provider/rules"),

  createRule: (data: { stablecoin?: string; providerCode: string; priority: number; role?: string; isActive?: boolean }) =>
    request<any>("/api/admin/liquidity-provider/rules", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateRule: (id: string, data: { priority?: number; role?: string; isActive?: boolean }) =>
    request<any>(`/api/admin/liquidity-provider/rules/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteRule: (id: string) =>
    request<{ success: boolean }>(`/api/admin/liquidity-provider/rules/${id}`, {
      method: "DELETE",
    }),

  reorderRules: (data: { rules: Array<{ id: string; priority: number }> }) =>
    request<any>("/api/admin/liquidity-provider/rules/reorder", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Provider Account API (genérico - substitui CAInvest Account)
export const providerAccountApi = {
  listProviderAccounts: () =>
    request<Array<{
      providerCode: string;
      displayName: string;
      brlBalance: number;
      brlBalanceFormatted: string;
      usdBalance: number;
      usdBalanceFormatted: string;
      lastSyncAt?: string;
      syncStatus?: string;
      alerts: { lowBrlBalance: boolean; lowUsdBalance: boolean };
    }>>("/api/admin/provider-account"),

  getSummary: (code: string) =>
    request<{
      providerCode: string;
      displayName: string;
      brlBalance: number;
      brlBalanceFormatted: string;
      usdBalance: number;
      usdBalanceFormatted: string;
      lastSyncAt?: string;
      syncStatus?: string;
      lastSyncBrlBalance?: number;
      lastSyncUsdBalance?: number;
      alerts: { lowBrlBalance: boolean; lowUsdBalance: boolean };
    }>(`/api/admin/provider-account/${code}/summary`),

  getMovements: (code: string, filters?: { type?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const query = params.toString();
    return request<{
      items: Array<{
        id: string;
        providerCode: string;
        type: string;
        brlAmount: number;
        usdAmount: number;
        brlBalanceAfter: number;
        usdBalanceAfter: number;
        externalReference?: string;
        description?: string;
        tradeId?: string;
        currencyConversionId?: string;
        createdAt: string;
        createdByName?: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/provider-account/${code}/movements${query ? `?${query}` : ""}`);
  },

  recordDeposit: (code: string, data: { brlAmount?: number; usdAmount?: number; description?: string }) =>
    request<any>(`/api/admin/provider-account/${code}/deposit`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  recordWithdrawal: (code: string, data: { brlAmount?: number; usdAmount?: number; description?: string }) =>
    request<any>(`/api/admin/provider-account/${code}/withdrawal`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  recordAdjustment: (code: string, data: { brlAmount?: number; usdAmount?: number; description: string }) =>
    request<any>(`/api/admin/provider-account/${code}/adjustment`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  syncWithApi: (code: string) =>
    request<{
      success: boolean;
      error?: string;
      apiBrlBalance?: number;
      apiUsdBalance?: number;
      status?: string;
    }>(`/api/admin/provider-account/${code}/sync`, { method: "POST" }),

  getConfig: (code: string) =>
    request<{
      providerCode: string;
      displayName: string;
      minBrlBalance: number;
      minUsdBalance: number;
      alertEmail?: string;
    }>(`/api/admin/provider-account/${code}/config`),

  updateConfig: (code: string, data: { minBrlBalance?: number; minUsdBalance?: number; alertEmail?: string }) =>
    request<any>(`/api/admin/provider-account/${code}/config`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// ==================== HEDGE / PROTEÇÃO CAMBIAL ====================

// Types
export interface HedgeInvoice {
  id: string;
  companyId: string;
  invoiceNumber: string;
  supplierName: string;
  supplierCountry?: string;
  amountUsdc: string;
  dueDate: string;
  description?: string;
  fileUrl?: string;
  fileName?: string;
  fileMimeType?: string;
  extractedData?: Record<string, unknown>;
  extractionConfidence?: number;
  manuallyEdited: boolean;
  status: "DRAFT" | "CONFIRMED" | "HEDGED" | "PAID";
  createdAt: string;
  updatedAt: string;
}

export interface HedgeQuote {
  id: string;
  spotRate: string;
  finalRate: string;
  lockedAmountBrl: string;
  // Fee de Proteção (novo modelo)
  protectionFeeBrl: string;
  operationFeeBrl: string;
  capitalCostBrl: string;
  durationMonths: string;
  // Garantia
  guaranteeAmountUsdc: string;
  guaranteePercentage: string;
  validUntil: string;
  isValid: boolean;
  // DEPRECATED: Campos antigos mantidos para compatibilidade
  spreadPercentage?: string;
  spreadRate?: string;
  spreadAmountBrl?: string;
}

export interface Hedge {
  id: string;
  hedgeCode: string;
  companyId: string;
  invoiceId: string;
  hedgeAmountUsdc: string;
  guaranteeAmountUsdc: string;
  guaranteePercentage: string;
  lockedAmountBrl: string;
  spotExchangeRate: string;
  lockedExchangeRate: string;
  settlementSpotRate?: string;
  // Fee de Proteção (novo modelo)
  protectionFeeBrl?: string;
  operationFeeBrl?: string;
  capitalCostBrl?: string;
  // DEPRECATED: Campos antigos mantidos para compatibilidade
  spreadPercentage?: string;
  spreadAmountBrl?: string;
  status: "PENDING_GUARANTEE" | "PENDING_APPROVAL" | "ACTIVE" | "PENDING_SETTLEMENT" | "SETTLED" | "REJECTED" | "DEFAULTED" | "CANCELLED";
  dueDate: string;
  gracePeriodEndDate?: string;
  createdAt: string;
  guaranteeDepositedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  settledAt?: string;
  rejectionReason?: string;
  notes?: string;
  invoice?: HedgeInvoice;
  company?: { id: string; razaoSocial: string; cnpj: string };
  // Renovação de cotação
  quoteRefreshedAt?: string;
  quoteValidUntil?: string;
  // Campos do termo
  termGeneratedAt?: string;
  termAcceptedAt?: string;
  termHash?: string;
}

export interface HedgeSettings {
  id: string;
  guaranteePercentage: string;
  // Fee de Proteção (novo modelo)
  operationFeePercentage: string;
  minOperationFeeBrl: string;
  capitalCostPercentage: string;
  // DEPRECATED: Campos antigos mantidos para compatibilidade
  spreadPercentage?: string;
  minSpreadBrl?: string;
  maxSpreadPercentage: string;
  minHedgeAmountUsdc: string;
  maxHedgeDurationDays?: number;
  gracePeriodDays: number;
  requireApproval: boolean;
  updatedAt: string;
  updatedBy?: string;
}

export interface HedgeDashboard {
  activeHedgesCount: number;
  totalVolumeUsdc: string;
  totalGuaranteesUsdc: string;
  realizedRevenueBrl: string;
  projectedRevenueBrl: string;
  hedgesAtRiskCount: number;
  pendingApprovalCount: number;
  nextDueDate?: string;
}

// Admin Hedges API
export const hedgesAdminApi = {
  // Dashboard
  getDashboard: () => request<HedgeDashboard>("/api/admin/hedges/dashboard"),

  // Settings
  getSettings: () => request<HedgeSettings>("/api/admin/hedges/settings"),

  updateSettings: (data: Partial<{
    guaranteePercentage: number;
    // Fee de Proteção (novo modelo)
    operationFeePercentage: number;
    minOperationFeeBrl: number;
    capitalCostPercentage: number;
    // Limites
    maxSpreadPercentage: number;
    minHedgeAmountUsdc: number;
    maxHedgeDurationDays: number;
    gracePeriodDays: number;
    requireApproval: boolean;
    // DEPRECATED
    spreadPercentage?: number;
    minSpreadBrl?: number;
  }>) =>
    request<HedgeSettings>("/api/admin/hedges/settings", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // List & Get
  list: (options?: { page?: number; limit?: number; status?: string; companyId?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.status) params.append("status", options.status);
    if (options?.companyId) params.append("companyId", options.companyId);
    const query = params.toString();
    return request<{ items: Hedge[]; total: number; page: number; limit: number }>(
      `/api/admin/hedges${query ? `?${query}` : ""}`
    );
  },

  getById: (id: string) => request<Hedge>(`/api/admin/hedges/${id}`),

  // Actions
  approve: (id: string, notes?: string) =>
    request<Hedge>(`/api/admin/hedges/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  reject: (id: string, reason: string) =>
    request<Hedge>(`/api/admin/hedges/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  markDefaulted: (id: string, notes?: string) =>
    request<Hedge>(`/api/admin/hedges/${id}/mark-defaulted`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  // Term PDF Download
  getTermPdf: async (hedgeId: string): Promise<Blob> => {
    const token = useAuthStore.getState().accessToken;
    const response = await fetch(`${API_URL}/api/admin/hedges/${hedgeId}/term-pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Erro ao baixar termo");
    }
    return response.blob();
  },
};

// Company Hedges API
export const hedgesCompanyApi = {
  // Invoices
  parseInvoice: (data: { fileContent: string; fileName: string; mimeType: string }) =>
    request<{
      success: boolean;
      data?: {
        invoiceNumber?: string;
        supplierName?: string;
        supplierCountry?: string;
        currency?: string;
        originalAmount?: number;
        amountUsdc?: number;
        dueDate?: string;
        description?: string;
      };
      rawData?: Record<string, unknown>;
      confidence: number;
      error?: string;
    }>("/api/company/hedges/invoices/parse", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createInvoice: (data: {
    invoiceNumber: string;
    supplierName: string;
    supplierCountry?: string;
    amountUsdc: number;
    dueDate: string;
    description?: string;
    fileUrl?: string;
    fileName?: string;
    fileMimeType?: string;
    extractedData?: Record<string, unknown>;
    extractionConfidence?: number;
  }) =>
    request<HedgeInvoice>("/api/company/hedges/invoices", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateInvoice: (id: string, data: Partial<{
    invoiceNumber: string;
    supplierName: string;
    supplierCountry: string;
    amountUsdc: number;
    dueDate: string;
    description: string;
    manuallyEdited: boolean;
  }>) =>
    request<HedgeInvoice>(`/api/company/hedges/invoices/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  confirmInvoice: (id: string) =>
    request<HedgeInvoice>(`/api/company/hedges/invoices/${id}/confirm`, {
      method: "POST",
    }),

  listInvoices: (options?: { page?: number; limit?: number; status?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.status) params.append("status", options.status);
    const query = params.toString();
    return request<{ items: HedgeInvoice[]; total: number }>(
      `/api/company/hedges/invoices${query ? `?${query}` : ""}`
    );
  },

  getInvoice: (id: string) => request<HedgeInvoice>(`/api/company/hedges/invoices/${id}`),

  // Quotes
  requestQuote: (data: { invoiceId: string; hedgeAmountUsdc: number }) =>
    request<HedgeQuote>("/api/company/hedges/quotes", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Term Preview
  getTermPreview: (invoiceId: string, quoteId: string) =>
    request<{
      html: string;
      data: {
        hedgeCode: string;
        hedgeAmountUsdc: string;
        durationDays: number;
        startDate: string;
        endDate: string;
        lockedAmountBrl: string;
        protectionFeeBrl: string;
        companyName: string;
        companyCnpj: string;
      };
    }>(`/api/company/hedges/term-preview?invoiceId=${invoiceId}&quoteId=${quoteId}`),

  // Term PDF Download
  getTermPdf: async (hedgeId: string): Promise<Blob> => {
    const token = useAuthStore.getState().accessToken;
    const response = await fetch(`${API_URL}/api/company/hedges/${hedgeId}/term-pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Erro ao baixar termo");
    }
    return response.blob();
  },

  // Hedges
  // NOTA: acceptedTerms removido - hedge em PENDING_GUARANTEE é apenas um rascunho
  // O termo será aceito na página de garantia (depositGuarantee)
  createHedge: (data: { invoiceId: string; quoteId: string; notes?: string }) =>
    request<Hedge>("/api/company/hedges", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Renovar cotação para hedge aguardando garantia
  refreshQuote: (id: string) =>
    request<{
      hedgeId: string;
      original: {
        spotRate: string;
        finalRate: string;
        lockedAmountBrl: string;
        protectionFeeBrl: string;
        operationFeeBrl: string;
        capitalCostBrl: string;
      };
      updated: {
        spotRate: string;
        finalRate: string;
        lockedAmountBrl: string;
        protectionFeeBrl: string;
        operationFeeBrl: string;
        capitalCostBrl: string;
      };
      guaranteeAmountUsdc: string;
      validUntil: string;
      variationPercent: number;
    }>(`/api/company/hedges/${id}/refresh-quote`, {
      method: "POST",
    }),

  // Preview do termo para página de garantia (usa valores atuais do hedge)
  getTermPreviewForGuarantee: (hedgeId: string) =>
    request<{
      html: string;
      data: {
        hedgeCode: string;
        hedgeAmountUsdc: string;
        durationDays: number;
        startDate: string;
        endDate: string;
        lockedAmountBrl: string;
        protectionFeeBrl: string;
        companyName: string;
        companyCnpj: string;
      };
    }>(`/api/company/hedges/${hedgeId}/term-preview-guarantee`),

  // Depositar garantia (requer cotação renovada e aceite do termo)
  depositGuarantee: (id: string, data: { conversionId?: string; acceptedTerms: boolean }) =>
    request<Hedge>(`/api/company/hedges/${id}/deposit-guarantee`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listHedges: (options?: { page?: number; limit?: number; status?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.status) params.append("status", options.status);
    const query = params.toString();
    return request<{ items: Hedge[]; total: number; page: number; limit: number }>(
      `/api/company/hedges${query ? `?${query}` : ""}`
    );
  },

  getHedge: (id: string) => request<Hedge>(`/api/company/hedges/${id}`),

  settleHedge: (id: string, conversionId?: string) =>
    request<Hedge>(`/api/company/hedges/${id}/settle`, {
      method: "POST",
      body: JSON.stringify({ conversionId }),
    }),
};

// ─── Payment Gateway (Admin) ────────────────────────────

export interface PaymentGatewayConfig {
  id: string;
  providerCode: string;
  name: string;
  hasApiKey: boolean;
  hasApiSecret: boolean;
  baseUrl: string;
  environment: string;
  metadata: Record<string, unknown> | null;
  isActive: boolean;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentGatewayTestResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export const paymentGatewayAdminApi = {
  listConfigs: () =>
    request<PaymentGatewayConfig[]>("/api/admin/payment-gateway/configs"),

  getConfig: (id: string) =>
    request<PaymentGatewayConfig>(`/api/admin/payment-gateway/configs/${id}`),

  createConfig: (data: {
    providerCode: string;
    name: string;
    apiKey: string;
    apiSecret?: string;
    baseUrl: string;
    environment: string;
    metadata?: Record<string, unknown>;
    isActive?: boolean;
  }) =>
    request<PaymentGatewayConfig>("/api/admin/payment-gateway/configs", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateConfig: (
    id: string,
    data: {
      name?: string;
      apiKey?: string;
      apiSecret?: string;
      baseUrl?: string;
      environment?: string;
      metadata?: Record<string, unknown>;
      isActive?: boolean;
    },
  ) =>
    request<PaymentGatewayConfig>(`/api/admin/payment-gateway/configs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteConfig: (id: string) =>
    request(`/api/admin/payment-gateway/configs/${id}`, { method: "DELETE" }),

  testConfig: (id: string) =>
    request<PaymentGatewayTestResult>(
      `/api/admin/payment-gateway/configs/${id}/test`,
      { method: "POST" },
    ),
};

// ─── International Payments (Company) ───────────────────────────────

export interface PaymentBeneficiary {
  id: string;
  nickname: string;
  country: string;
  currency: string;
  paymentRail: string;
  beneficiaryName: string;
  bankName?: string;
  maskedRoutingNumber?: string;
  maskedBankAccountNumber?: string;
  maskedIban?: string;
  maskedSpeiClabe?: string;
  isActive: boolean;
  createdAt: string;
}

export interface PaymentRequest {
  id: string;
  paymentCode: string;
  status: string;
  currency: string;
  paymentRail: string;
  amount: string;
  amountUsdc: string | null;
  exchangeRate: string | null;
  serviceFeeAmount: string | null;
  serviceFeeType: string | null;
  serviceFeeRate: string | null;
  providerFlatFeeUsd: string | null;
  invoiceNumber: string | null;
  supplierName: string | null;
  description: string | null;
  beneficiary?: {
    id: string;
    nickname: string;
    beneficiaryName: string;
  } | null;
  inlineBeneficiaryName: string | null;
  inlineBankAccountNumber: string | null;
  inlineRoutingNumber: string | null;
  inlineBankName: string | null;
  inlineIban: string | null;
  inlineBic: string | null;
  inlineSpeiClabe: string | null;
  inlineSpeiBankCode: string | null;
  inlineAddressStreet: string | null;
  inlineAddressCity: string | null;
  inlineAddressState: string | null;
  inlineAddressPostalCode: string | null;
  inlineAddressCountry: string | null;
  inlineSepaCountry: string | null;
  inlineSepaType: string | null;
  inlineFirstName: string | null;
  inlineLastName: string | null;
  depositAddress: string | null;
  depositAmount: string | null;
  depositPaymentRail: string | null;
  depositTxHash: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  providerStatus: string | null;
  finalFiatAmount: string | null;
  initialCryptoAmount: string | null;
  providerActualFee: string | null;
  companyNotes: string | null;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentQuoteResult {
  currency: string;
  symbol: string;
  rate: number;
  expiresAt: string;
  providerFlatFeeUsd: number;
  estimatedDelivery: string;
  serviceFee?: {
    type: "FIXED" | "PERCENTAGE";
    rate: number;
    description: string;
  };
}

export interface ParsePaymentInvoiceResponse {
  success: boolean;
  data?: {
    invoiceNumber?: string;
    supplierName?: string;
    supplierCountry?: string;
    currency?: string;
    amount?: number;
    dueDate?: string;
    description?: string;
    beneficiaryName?: string;
    bankName?: string;
    bankAccountNumber?: string;
    routingNumber?: string;
    iban?: string;
    bic?: string;
    clabe?: string;
  };
  confidence: number;
  error?: string;
}

export const paymentsApi = {
  // Invoice parsing
  parseInvoice: (data: { fileBase64: string; fileName: string; fileMimeType: string }) =>
    request<ParsePaymentInvoiceResponse>("/api/company/payments/parse-invoice", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Quote
  getQuote: (currency: string) =>
    request<PaymentQuoteResult>(`/api/company/payments/quote?currency=${currency}`),

  // Payments
  createPayment: (data: {
    currency: string;
    paymentRail: string;
    amount: number;
    invoiceNumber?: string;
    supplierName?: string;
    description?: string;
    invoiceFileBase64?: string;
    invoiceFileName?: string;
    invoiceFileMimeType?: string;
    beneficiaryId?: string;
    inlineBeneficiaryName?: string;
    inlineBankAccountNumber?: string;
    inlineRoutingNumber?: string;
    inlineBankName?: string;
    inlineIban?: string;
    inlineBic?: string;
    inlineSpeiClabe?: string;
    inlineSpeiBankCode?: string;
    inlineAddressStreet?: string;
    inlineAddressCity?: string;
    inlineAddressState?: string;
    inlineAddressPostalCode?: string;
    inlineAddressCountry?: string;
    inlineSepaCountry?: string;
    inlineSepaType?: string;
    inlineFirstName?: string;
    inlineLastName?: string;
    saveBeneficiary?: boolean;
    beneficiaryNickname?: string;
    companyNotes?: string;
  }) =>
    request<PaymentRequest>("/api/company/payments", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listPayments: (options?: { page?: number; limit?: number; status?: string; currency?: string; search?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.status && options.status !== "all") params.append("status", options.status);
    if (options?.currency && options.currency !== "all") params.append("currency", options.currency);
    if (options?.search) params.append("search", options.search);
    const query = params.toString();
    return request<{ items: PaymentRequest[]; total: number; page: number; limit: number }>(
      `/api/company/payments${query ? `?${query}` : ""}`
    );
  },

  getPayment: (id: string) => request<PaymentRequest>(`/api/company/payments/${id}`),

  cancelPayment: (id: string) =>
    request<void>(`/api/company/payments/${id}/cancel`, { method: "POST" }),

  // Beneficiaries
  listBeneficiaries: () =>
    request<PaymentBeneficiary[]>("/api/company/payments/beneficiaries"),

  createBeneficiary: (data: {
    nickname: string;
    country: string;
    currency: string;
    paymentRail: string;
    beneficiaryName: string;
    routingNumber?: string;
    bankAccountNumber?: string;
    bankName?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressPostalCode?: string;
    addressCountry?: string;
    iban?: string;
    bic?: string;
    sepaCountry?: string;
    sepaType?: string;
    firstName?: string;
    lastName?: string;
    speiClabe?: string;
    speiBankCode?: string;
  }) => {
    // Map flat address fields to nested address object for backend DTO
    const { addressStreet, addressCity, addressState, addressPostalCode, addressCountry, ...rest } = data;
    const payload: Record<string, unknown> = { ...rest };
    if (addressStreet) {
      payload.address = {
        streetLine1: addressStreet,
        city: addressCity || "",
        state: addressState || "",
        postalCode: addressPostalCode || "",
        country: addressCountry || "",
      };
    }
    return request<PaymentBeneficiary>("/api/company/payments/beneficiaries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateBeneficiary: (id: string, data: Partial<{
    nickname: string;
    beneficiaryName: string;
    bankName: string;
    routingNumber: string;
    bankAccountNumber: string;
    addressStreet: string;
    addressCity: string;
    addressState: string;
    addressPostalCode: string;
    addressCountry: string;
    iban: string;
    bic: string;
    sepaCountry: string;
    sepaType: string;
    firstName: string;
    lastName: string;
    speiClabe: string;
    speiBankCode: string;
  }>) => {
    // Map flat address fields to nested address object for backend DTO
    const { addressStreet, addressCity, addressState, addressPostalCode, addressCountry, ...rest } = data;
    const payload: Record<string, unknown> = { ...rest };
    if (addressStreet) {
      payload.address = {
        streetLine1: addressStreet,
        city: addressCity || "",
        state: addressState || "",
        postalCode: addressPostalCode || "",
        country: addressCountry || "",
      };
    }
    return request<PaymentBeneficiary>(`/api/company/payments/beneficiaries/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  deleteBeneficiary: (id: string) =>
    request<void>(`/api/company/payments/beneficiaries/${id}`, { method: "DELETE" }),
};

// ─── International Payments (Admin) ─────────────────────────────────

export interface PaymentSettings {
  feeEnabled: boolean;
  feeType: "FIXED" | "PERCENTAGE";
  feeFixedAmountUsd: number;
  feePercentage: number;
  minFeeUsd: number;
  maxFeeUsd: number | null;
  providerFeePercentage?: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PaymentsDashboard {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  volumeUsd: string;
  volumeEur: string;
  volumeMxn: string;
}

export const adminPaymentsApi = {
  listPayments: (options?: { page?: number; limit?: number; status?: string; currency?: string; companyId?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.status && options.status !== "all") params.append("status", options.status);
    if (options?.currency && options.currency !== "all") params.append("currency", options.currency);
    if (options?.companyId) params.append("companyId", options.companyId);
    const query = params.toString();
    return request<{ items: PaymentRequest[]; total: number; page: number; limit: number }>(
      `/api/admin/payments${query ? `?${query}` : ""}`
    );
  },

  getPayment: (id: string) => request<PaymentRequest>(`/api/admin/payments/${id}`),

  approvePayment: (id: string, data?: { adminNotes?: string }) =>
    request<void>(`/api/admin/payments/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  rejectPayment: (id: string, data: { reason: string }) =>
    request<void>(`/api/admin/payments/${id}/reject`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getDashboard: () =>
    request<PaymentsDashboard>("/api/admin/payments/dashboard"),

  processPayments: () =>
    request<{ success: boolean; message: string }>("/api/admin/payments/process", {
      method: "POST",
    }),

  getSettings: () =>
    request<PaymentSettings>("/api/admin/payments/settings"),

  updateSettings: (data: Partial<PaymentSettings>) =>
    request<PaymentSettings>("/api/admin/payments/settings", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};
